import apiClient from './api';
import { getApiAccessToken } from './UserService';
import {
    ExportColumn,
    ExportableColumnsResponse,
    ClientExportConfig,
    UpdateColumnActiveRequest,
    UpdateColumnVisibilityRequest,
    BulkUpdateVisibilityRequest,
    AddExportColumnRequest
} from '../types/ExportConfig';

const baseUrl = '/api/ExportConfig';

/**
 * Gets all exportable columns for a specific address system.
 * A column is exportable if it's globally active AND visible for the address system.
 * If no config exists for the address system, it will be auto-created.
 */
export const getExportableColumns = async (addressSystemId: number): Promise<ExportableColumnsResponse> => {
    const token = await getApiAccessToken();
    const response = await apiClient.get(`${baseUrl}/exportable/${addressSystemId}`, {
        headers: {
            Authorization: `Bearer ${token}`
        }
    });
    return response.data as ExportableColumnsResponse;
};

// #region Admin Management Functions

/**
 * Gets all export columns with their global active status (for admin panel)
 */
export const getAllExportColumns = async (): Promise<ExportColumn[]> => {
    const token = await getApiAccessToken();
    const response = await apiClient.get(`${baseUrl}/columns`, {
        headers: {
            Authorization: `Bearer ${token}`
        }
    });
    return response.data as ExportColumn[];
};

/**
 * Gets the client export config for a specific address system (for admin panel)
 */
export const getClientExportConfig = async (addressSystemId: number): Promise<ClientExportConfig[]> => {
    const token = await getApiAccessToken();
    const response = await apiClient.get(`${baseUrl}/config/${addressSystemId}`, {
        headers: {
            Authorization: `Bearer ${token}`
        }
    });
    return response.data as ClientExportConfig[];
};

/**
 * Updates the global active status of an export column
 */
export const updateColumnActiveStatus = async (request: UpdateColumnActiveRequest): Promise<boolean> => {
    const token = await getApiAccessToken();
    const response = await apiClient.put(`${baseUrl}/columns/active`, request, {
        headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json'
        }
    });
    return response.status === 200;
};

/**
 * Updates the visibility of a column for a specific address system
 */
export const updateColumnVisibility = async (request: UpdateColumnVisibilityRequest): Promise<boolean> => {
    const token = await getApiAccessToken();
    const response = await apiClient.put(`${baseUrl}/config/visibility`, request, {
        headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json'
        }
    });
    return response.status === 200;
};

/**
 * Bulk updates visibility for multiple columns for an address system
 */
export const bulkUpdateVisibility = async (request: BulkUpdateVisibilityRequest): Promise<boolean> => {
    const token = await getApiAccessToken();
    const response = await apiClient.put(`${baseUrl}/config/visibility/bulk`, request, {
        headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json'
        }
    });
    return response.status === 200;
};

/**
 * Adds a new export column
 */
export const addExportColumn = async (request: AddExportColumnRequest): Promise<ExportColumn | null> => {
    const token = await getApiAccessToken();
    try {
        const response = await apiClient.post(`${baseUrl}/columns`, request, {
            headers: {
                Authorization: `Bearer ${token}`,
                'Content-Type': 'application/json'
            }
        });
        return response.data as ExportColumn;
    } catch (error: any) {
        if (error.response?.status === 409) {
            // Column already exists
            return null;
        }
        throw error;
    }
};

/**
 * Deletes an export column
 */
export const deleteExportColumn = async (columnId: number): Promise<boolean> => {
    const token = await getApiAccessToken();
    const response = await apiClient.delete(`${baseUrl}/columns/${columnId}`, {
        headers: {
            Authorization: `Bearer ${token}`
        }
    });
    return response.status === 200;
};

// #endregion
